import React, { useState, useEffect } from 'react';
import MetaTags from 'react-meta-tags';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Container, Button, Row, Col, Card, CardBody } from 'reactstrap';
import paginationFactory, {
  PaginationProvider,
  PaginationListStandalone,
  SizePerPageDropdownStandalone,
} from 'react-bootstrap-table2-paginator';
import ToolkitProvider, { Search } from 'react-bootstrap-table2-toolkit';
import BootstrapTable from 'react-bootstrap-table-next';

import '../../assets/scss/datatables.scss';
import { getFirebaseBackend } from 'helpers/firebaseHelper';
import { translateArray, capitalizeString } from 'utils';
import { getDefaultSorted, getPageOptions } from './config/tableConfig';
import { getAccountColumns } from './config/accountTableColumns';
import { useStatusCustomization } from 'hooks/useStatusCustomization';
import EditButton from '../../components/Edit-Button';
import TableLoading from 'components/Table-Loading';

const ListAccounts = () => {
  const firebaseHelper = getFirebaseBackend();
  const statusMap = useStatusCustomization();
  const { t: translate } = useTranslation();
  const { SearchBar } = Search;
  const [userData, setUserData] = useState([]);
  const [accounts, setAccounts] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchAllAccounts = async () => {
    const accounts = await firebaseHelper.getAllAccounts();
    setAccounts(accounts ?? {});
  };

  useEffect(() => {
    if (!accounts) return;

    const newUserData = Object.values(accounts).map((accountInfo) => {
      return {
        id: accountInfo.id,
        name: accountInfo.name,
        services:
          accountInfo.services
            .slice(0, 4)
            .map((service) => translateArray(capitalizeString(service)))
            .join(', ') +
          (accountInfo.services.length > 4 ? `, +${accountInfo.services.length - 4}` : ''),
        begin: new Date(accountInfo.beginDate).toLocaleDateString(),
        end: accountInfo.endDate ? new Date(accountInfo.endDate).toLocaleDateString() : 'N/A',
        status: statusMap[accountInfo.status === true ? 'Activated' : 'Deactivated'],
        action: <EditButton link={`/edit-account?id=${accountInfo.id}`} />,
      };
    });
    setUserData(newUserData);
    setIsLoading(false);
  }, [accounts]);

  useEffect(() => {
    fetchAllAccounts();
  }, []);

  return (
    <React.Fragment>
      <div className="page-content" style={{ minHeight: '100vh' }}>
        <MetaTags>
          <title>{translate('List Accounts')} | Ectools</title>
        </MetaTags>
        <Container fluid>
          <Row>
            <Col xs="12" className="mb-4">
              <h4 className="mb-0 font-size-18">{translate('Accounts')}</h4>
            </Col>
          </Row>

          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <PaginationProvider
                    pagination={paginationFactory(getPageOptions(userData.length))}
                    keyField="id"
                    columns={getAccountColumns(translate)}
                    data={userData}
                  >
                    {({ paginationProps, paginationTableProps }) => (
                      <ToolkitProvider
                        keyField="id"
                        columns={getAccountColumns(translate)}
                        data={userData}
                        search
                      >
                        {(toolkitProps) => (
                          <React.Fragment>
                            <Row className="mb-2 d-flex justify-content-between">
                              <Col md="4">
                                <div className="search-box me-2 mb-2 d-inline-block">
                                  <div className="position-relative">
                                    <SearchBar
                                      {...toolkitProps.searchProps}
                                      placeholder={translate('Search')}
                                    />
                                    <i className="bx bx-search-alt search-icon" />
                                  </div>
                                </div>
                              </Col>
                              <Col md="4" className="d-flex flex-row-reverse align-items-center">
                                <Link to="/create-account">
                                  <Button
                                    color="primary"
                                    className="d-flex gap-1 align-items-center"
                                  >
                                    <i className="bx bx-plus-circle" />
                                    {translate('Create new Account')}
                                  </Button>
                                </Link>
                              </Col>
                            </Row>

                            <Row>
                              <Col xl="12">
                                <div className="table-responsive">
                                  {isLoading && <TableLoading />}
                                  {/* The table stays mounted: the pagination and search providers fail without it */}
                                  <div className={isLoading ? 'd-none' : ''}>
                                    <BootstrapTable
                                      keyField={'id'}
                                      responsive
                                      hover
                                      bordered={false}
                                      striped={false}
                                      defaultSorted={getDefaultSorted}
                                      classes={'table align-middle table-nowrap'}
                                      headerWrapperClasses={'thead-light'}
                                      {...toolkitProps.baseProps}
                                      {...paginationTableProps}
                                    />
                                  </div>
                                </div>
                              </Col>
                            </Row>

                            <Row className="align-items-md-center mt-30">
                              <Col className="inner-custom-pagination d-flex">
                                <div className="d-inline">
                                  <SizePerPageDropdownStandalone {...paginationProps} />
                                </div>
                                <div className="text-md-right ms-auto">
                                  <PaginationListStandalone {...paginationProps} />
                                </div>
                              </Col>
                            </Row>
                          </React.Fragment>
                        )}
                      </ToolkitProvider>
                    )}
                  </PaginationProvider>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ListAccounts;
